import {
  Backdrop,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Drawer,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useAppDispatch, useAppSelector } from "features/store";
import { pushNotification } from "features/notificationSlice";
import UnsavedPlatformDraftsDialog from "components/userManagement/UnsavedPlatformDraftsDialog";
import { Add, Tune, VpnKey } from "@mui/icons-material";
import FileSearch from "components/fileStorage/FileSearch";
import PaginationControls from "components/fileStorage/PaginationControls";
import Layout from "components/Layout";
import { TABLE_PAGE_CONTAINER_HEIGHT } from "config";
import SftpConnectionsTable from "components/sftp/SftpConnectionsTable";
import { bucketFolderSortKey } from "components/sftp/sftpFolderMountUtils";
import SftpConnectionPanel from "components/sftp/SftpConnectionPanel";
import SftpConnectionDetail from "components/sftp/SftpConnectionDetail";
import HelperGenerateKeyPairDialog from "components/sftp/HelperGenerateKeyPairDialog";
import { useGetSftpConnectionsQuery } from "features/sftp/sftp";
import { setSftpNavigationUnsaved } from "features/sftp/sftpNavigationUnsaved";
import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import useAppNavigate from "hooks/useAppNavigate";
import { usePermissions } from "hooks/usePermissions";
import { ACTIONS } from "utils/access";
import {
  resolveSftpAuthMode,
  type SFTPConnection,
  type SFTPConnectionSortBy,
} from "types/interfaces/sftp";

const DEFAULT_PAGE_SIZE = 50;

export default function SftpManagementPage() {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useAppNavigate();
  const { id } = useParams<{ id?: string }>();

  const { mayDo } = usePermissions();
  const canEditSftp = mayDo(ACTIONS.SFTP_MANAGEMENT_EDIT);

  const {
    data: connections = [],
    isLoading,
    isFetching,
  } = useGetSftpConnectionsQuery();

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    "active" | "inactive" | undefined
  >(undefined);
  const [authFilter, setAuthFilter] = useState<
    "password" | "key" | "both" | undefined
  >(undefined);
  const [roleFilter, setRoleFilter] = useState<"read" | "write" | undefined>(
    undefined,
  );
  const [sortBy, setSortBy] = useState<SFTPConnectionSortBy | undefined>(
    undefined,
  );
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [selectedId, setSelectedId] = useState<number | null>(
    id ? parseInt(id, 10) : null,
  );
  const [panelMode, setPanelMode] = useState<"detail" | "create" | null>(
    id ? "detail" : null,
  );
  const [isPanelSaving, setIsPanelSaving] = useState(false);
  const [selectedConnectionUnsaved, setSelectedConnectionUnsaved] =
    useState(false);
  const [createPanelDirty, setCreatePanelDirty] = useState(false);
  const [sftpLeaveDialogOpen, setSftpLeaveDialogOpen] = useState(false);
  const [helperKeyDialogOpen, setHelperKeyDialogOpen] = useState(false);
  const [mobileOptionsDrawerOpen, setMobileOptionsDrawerOpen] = useState(false);
  const [sftpPanelResetKey, setSftpPanelResetKey] = useState(0);
  const pendingSftpLeaveRef = useRef<(() => void) | null>(null);

  const isSftpPanelDirty =
    (panelMode === "detail" && selectedConnectionUnsaved) ||
    (panelMode === "create" && createPanelDirty);

  useEffect(() => {
    const dirty =
      (panelMode === "detail" && selectedConnectionUnsaved) ||
      (panelMode === "create" && createPanelDirty);
    setSftpNavigationUnsaved(dirty);
    return () => setSftpNavigationUnsaved(false);
  }, [panelMode, selectedConnectionUnsaved, createPanelDirty]);

  useEffect(() => {
    if (!isSftpPanelDirty || panelMode === null) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [isSftpPanelDirty, panelMode]);

  const showToast = (message: string, severity: "success" | "error") => {
    dispatch(pushNotification({ message, severity }));
  };

  const filtered = useMemo(() => {
    let list = connections;
    if (statusFilter === "active") list = list.filter((c) => c.is_active);
    else if (statusFilter === "inactive")
      list = list.filter((c) => !c.is_active);
    if (authFilter === "password") {
      list = list.filter((c) => resolveSftpAuthMode(c) === "password");
    } else if (authFilter === "key") {
      list = list.filter((c) => resolveSftpAuthMode(c) === "key");
    } else if (authFilter === "both") {
      list = list.filter((c) => resolveSftpAuthMode(c) === "both");
    }
    if (roleFilter === "read") {
      list = list.filter((c) => (c.role || "read").toLowerCase() !== "write");
    } else if (roleFilter === "write") {
      list = list.filter((c) => (c.role || "read").toLowerCase() === "write");
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          (c.client_name || "").toLowerCase().includes(q) ||
          c.username.toLowerCase().includes(q) ||
          c.bucket.toLowerCase().includes(q),
      );
    }
    return list;
  }, [connections, search, statusFilter, authFilter, roleFilter]);

  const sorted = useMemo(() => {
    if (!sortBy) return filtered;
    const dir = sortOrder === "asc" ? 1 : -1;
    const list = [...filtered];
    list.sort((a, b) => {
      switch (sortBy) {
        case "name": {
          const an = (a.name || a.username || "").toLowerCase();
          const bn = (b.name || b.username || "").toLowerCase();
          return an.localeCompare(bn) * dir;
        }
        case "client_name": {
          const ac = (a.client_name || "").toLowerCase();
          const bc = (b.client_name || "").toLowerCase();
          return ac.localeCompare(bc) * dir;
        }
        case "bucket_folder": {
          return (
            bucketFolderSortKey(a).localeCompare(bucketFolderSortKey(b)) * dir
          );
        }
        case "created_at":
          return (
            (new Date(a.created_at).getTime() -
              new Date(b.created_at).getTime()) *
            dir
          );
        default:
          return 0;
      }
    });
    return list;
  }, [filtered, sortBy, sortOrder]);

  const totalItems = sorted.length;
  const totalPages = Math.ceil(totalItems / pageSize) || 1;
  const paginated = sorted.slice((page - 1) * pageSize, page * pageSize);

  const handleSortChange = useCallback(
    (field: SFTPConnectionSortBy) => {
      if (sortBy === field) {
        if (sortOrder === "asc") {
          setSortOrder("desc");
        } else if (sortOrder === "desc") {
          setSortBy(undefined);
          setSortOrder("asc");
        }
      } else {
        setSortBy(field);
        setSortOrder("asc");
      }
      setPage(1);
    },
    [sortBy, sortOrder],
  );

  const panelOpen = panelMode !== null;

  const handleSftpLeaveDiscard = () => {
    const fn = pendingSftpLeaveRef.current;
    pendingSftpLeaveRef.current = null;
    setSftpLeaveDialogOpen(false);
    setSftpPanelResetKey((k) => k + 1);
    setSelectedConnectionUnsaved(false);
    setCreatePanelDirty(false);
    if (fn) fn();
  };

  const handleManage = (conn: SFTPConnection) => {
    if (selectedId === conn.id && panelMode === "detail") {
      if (isSftpPanelDirty) {
        pendingSftpLeaveRef.current = () => {
          setSelectedId(null);
          setPanelMode(null);
          setIsPanelSaving(false);
          navigate("/sftp-management");
        };
        setSftpLeaveDialogOpen(true);
        return;
      }
      setSelectedId(null);
      setPanelMode(null);
      setSelectedConnectionUnsaved(false);
      setCreatePanelDirty(false);
      navigate("/sftp-management");
      return;
    }
    if (isSftpPanelDirty) {
      pendingSftpLeaveRef.current = () => {
        setSelectedId(conn.id);
        setPanelMode("detail");
        setIsPanelSaving(false);
        navigate(`/sftp-management/${conn.id}`);
      };
      setSftpLeaveDialogOpen(true);
      return;
    }
    setSelectedId(conn.id);
    setPanelMode("detail");
    navigate(`/sftp-management/${conn.id}`);
  };

  const handleCreate = () => {
    if (!canEditSftp) return;
    if (isSftpPanelDirty) {
      pendingSftpLeaveRef.current = () => {
        setSelectedId(null);
        setPanelMode("create");
        setIsPanelSaving(false);
        navigate("/sftp-management");
      };
      setSftpLeaveDialogOpen(true);
      return;
    }
    setSelectedId(null);
    setSelectedConnectionUnsaved(false);
    setCreatePanelDirty(false);
    setPanelMode("create");
    navigate("/sftp-management");
  };

  const handlePanelClose = () => {
    setPanelMode(null);
    setSelectedId(null);
    setSelectedConnectionUnsaved(false);
    setCreatePanelDirty(false);
    setIsPanelSaving(false);
    navigate("/sftp-management");
  };

  const handleSaved = (conn: SFTPConnection) => {
    const newId = conn?.id;
    if (newId == null || Number.isNaN(Number(newId))) {
      showToast("Connection created. Open it again from the list.", "success");
      setSelectedId(null);
      setPanelMode(null);
      setSelectedConnectionUnsaved(false);
      setCreatePanelDirty(false);
      navigate("/sftp-management");
      return;
    }
    showToast("Connection created.", "success");
    setSelectedId(newId);
    setPanelMode("detail");
    setSelectedConnectionUnsaved(false);
    setCreatePanelDirty(false);
    navigate(`/sftp-management/${newId}`);
  };

  const handleDeleted = () => {
    showToast("Connection deleted.", "success");
    setSelectedId(null);
    setPanelMode(null);
    setSelectedConnectionUnsaved(false);
    setCreatePanelDirty(false);
    setIsPanelSaving(false);
    navigate("/sftp-management");
  };

  return (
    <Layout title="SFTP Management" content="SFTP Management">
      <Container
        component="main"
        maxWidth={false}
        sx={{
          py: 3,
          px: { xs: 1.5, sm: 3 },
          height: TABLE_PAGE_CONTAINER_HEIGHT,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Toolbar: mobile = search + settings; desktop = search, actions, chips */}
        <Box mb={2} display="flex" flexDirection="column" gap={1}>
          {isMobile ? (
            <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <FileSearch
                  onSearch={(term) => {
                    setSearch(term || "");
                    setPage(1);
                  }}
                  placeholder="Search connections..."
                  autoComplete="off"
                />
              </Box>
              <IconButton
                color="primary"
                aria-label="Open filters and actions"
                onClick={() => setMobileOptionsDrawerOpen(true)}
                sx={{
                  flexShrink: 0,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                }}
              >
                <Tune />
              </IconButton>
            </Box>
          ) : (
            <>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                gap={2}
                flexWrap="wrap"
                sx={{ width: "100%" }}
              >
                <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                  <Box sx={{ minWidth: { xs: 0, sm: 220 }, maxWidth: 400 }}>
                    <FileSearch
                      onSearch={(term) => {
                        setSearch(term || "");
                        setPage(1);
                      }}
                      placeholder="Search connections..."
                      autoComplete="off"
                    />
                  </Box>
                  <Tooltip
                    title={
                      !canEditSftp
                        ? "Super User required to create SFTP connections."
                        : ""
                    }
                  >
                    <span>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleCreate}
                        disabled={!canEditSftp}
                        sx={{ textTransform: "none", fontWeight: 700 }}
                      >
                        <Add sx={{ fontSize: 18, mr: 0.5 }} />
                        New Connection
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
                <Tooltip
                  title={
                    !canEditSftp
                      ? "Super User required to generate key bundles."
                      : ""
                  }
                >
                  <span>
                    <Button
                      variant="outlined"
                      color="primary"
                      startIcon={<VpnKey />}
                      onClick={() => {
                        if (!canEditSftp) return;
                        setHelperKeyDialogOpen(true);
                      }}
                      disabled={!canEditSftp}
                      sx={{
                        textTransform: "none",
                        fontWeight: 700,
                        flexShrink: 0,
                      }}
                    >
                      Helper: Generate key pair
                    </Button>
                  </span>
                </Tooltip>
              </Box>

              <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
                {(["active", "inactive"] as const).map((f) => (
                  <Chip
                    key={f}
                    label={f.charAt(0).toUpperCase() + f.slice(1)}
                    size="small"
                    clickable
                    onClick={() => {
                      setStatusFilter((prev) => (prev === f ? undefined : f));
                      setPage(1);
                    }}
                    sx={{
                      fontWeight: 600,
                      fontSize: 12,
                      bgcolor: statusFilter === f ? "primary.main" : undefined,
                      color:
                        statusFilter === f ? "primary.contrastText" : undefined,
                      "& .MuiChip-label": { px: 1.25 },
                    }}
                  />
                ))}
                <Box
                  sx={{ width: "1px", height: 20, bgcolor: "divider", mx: 0.5 }}
                />
                {(["password", "key", "both"] as const).map((f) => (
                  <Chip
                    key={`auth-${f}`}
                    label={f.charAt(0).toUpperCase() + f.slice(1)}
                    size="small"
                    clickable
                    onClick={() => {
                      setAuthFilter((prev) => (prev === f ? undefined : f));
                      setPage(1);
                    }}
                    sx={{
                      fontWeight: 600,
                      fontSize: 12,
                      bgcolor: authFilter === f ? "primary.main" : undefined,
                      color:
                        authFilter === f ? "primary.contrastText" : undefined,
                      "& .MuiChip-label": { px: 1.25 },
                    }}
                  />
                ))}
                <Box
                  sx={{ width: "1px", height: 20, bgcolor: "divider", mx: 0.5 }}
                />
                {(["read", "write"] as const).map((f) => (
                  <Chip
                    key={`role-${f}`}
                    label={f.charAt(0).toUpperCase() + f.slice(1)}
                    size="small"
                    clickable
                    onClick={() => {
                      setRoleFilter((prev) => (prev === f ? undefined : f));
                      setPage(1);
                    }}
                    sx={{
                      fontWeight: 600,
                      fontSize: 12,
                      bgcolor: roleFilter === f ? "primary.main" : undefined,
                      color:
                        roleFilter === f ? "primary.contrastText" : undefined,
                      "& .MuiChip-label": { px: 1.25 },
                    }}
                  />
                ))}
              </Box>
            </>
          )}
        </Box>

        <Box
          sx={{
            position: "relative",
            display: "flex",
            flex: 1,
            minHeight: 0,
            borderRadius: 1,
            boxShadow: 1,
            border: "1px solid",
            borderColor: "divider",
            overflow: "hidden",
            bgcolor: "background.paper",
          }}
        >
          <Backdrop
            open={isPanelSaving}
            sx={{
              position: "absolute",
              zIndex: 1300,
              borderRadius: 1,
              bgcolor: (t) => alpha(t.palette.background.paper, 0.85),
            }}
          >
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              gap={1.5}
            >
              <CircularProgress sx={{ color: "primary.main" }} />
              <Typography
                variant="body2"
                fontWeight={600}
                color="text.secondary"
              >
                {panelMode === "create" ? "Creating…" : "Saving…"}
              </Typography>
            </Box>
          </Backdrop>
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: { xs: panelOpen ? "none" : "flex", sm: "flex" },
              flexDirection: "column",
            }}
          >
            <SftpConnectionsTable
              connections={paginated}
              isLoading={isLoading}
              isFetching={isFetching}
              selectedId={selectedId}
              onManage={handleManage}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSortChange={handleSortChange}
              hasUnsavedChangesForConnection={(cid) =>
                canEditSftp &&
                cid === selectedId &&
                panelMode === "detail" &&
                selectedConnectionUnsaved
              }
              pagination={
                totalItems > 0 ? (
                  <Box sx={{ borderTop: "1px solid", borderColor: "divider" }}>
                    <PaginationControls
                      currentLimit={pageSize}
                      onLimitChange={(limit) => {
                        setPageSize(limit);
                        setPage(1);
                      }}
                      totalItems={totalItems}
                      currentPage={page}
                      totalPages={totalPages}
                      onPageChange={setPage}
                      showLimitSelector={true}
                      showPageInfo={true}
                      size="small"
                      pageTokens={[]}
                      hasNextPage={page < totalPages}
                      borderStyle={{ borderTop: "none" }}
                    />
                  </Box>
                ) : null
              }
            />
          </Box>

          {/* Side panel: create only */}
          {panelMode === "create" && canEditSftp && (
            <SftpConnectionPanel
              key={`create-${sftpPanelResetKey}`}
              connection={null}
              onClose={handlePanelClose}
              onSaved={handleSaved}
              onDeleted={handleDeleted}
              onDirtyChange={setCreatePanelDirty}
              onSavingChange={setIsPanelSaving}
            />
          )}

          {/* Side panel: detail */}
          {panelMode === "detail" && selectedId !== null && (
            <Box
              sx={(theme) => ({
                width: { xs: "100%", sm: 420, md: 460 },
                maxWidth: "100%",
                flexShrink: 0,
                borderLeft: `1px solid ${theme.palette.divider}`,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              })}
            >
              <SftpConnectionDetail
                key={`${selectedId}-${sftpPanelResetKey}`}
                connectionId={selectedId}
                readOnly={!canEditSftp}
                onClose={handlePanelClose}
                onDeleted={handleDeleted}
                onDirtyChange={setSelectedConnectionUnsaved}
                onSavingChange={setIsPanelSaving}
              />
            </Box>
          )}
        </Box>
      </Container>

      <Drawer
        anchor="bottom"
        open={isMobile && mobileOptionsDrawerOpen}
        onClose={() => setMobileOptionsDrawerOpen(false)}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 2,
            borderTopRightRadius: 2,
            maxHeight: "min(88vh, 560px)",
            px: 2,
            py: 2,
          },
        }}
      >
        <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 2 }}>
          Options
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Tooltip
            title={
              !canEditSftp
                ? "Super User required to create SFTP connections."
                : ""
            }
          >
            <span>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={() => {
                  if (!canEditSftp) return;
                  setMobileOptionsDrawerOpen(false);
                  handleCreate();
                }}
                disabled={!canEditSftp}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  minHeight: 44,
                  borderRadius: 1,
                }}
              >
                <Add sx={{ fontSize: 18, mr: 0.5 }} />
                New Connection
              </Button>
            </span>
          </Tooltip>

          <Tooltip
            title={
              !canEditSftp
                ? "Generating key bundles needs Manage on SFTP Management."
                : ""
            }
          >
            <span>
              <Button
                fullWidth
                variant="outlined"
                color="primary"
                startIcon={<VpnKey />}
                onClick={() => {
                  if (!canEditSftp) return;
                  setMobileOptionsDrawerOpen(false);
                  setHelperKeyDialogOpen(true);
                }}
                disabled={!canEditSftp}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  minHeight: 44,
                  borderRadius: 1,
                }}
              >
                Helper: Generate key pair
              </Button>
            </span>
          </Tooltip>

          <Divider />

          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={700}
              sx={{ display: "block", mb: 1.25 }}
            >
              Status
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {(["active", "inactive"] as const).map((f) => (
                <Chip
                  key={f}
                  label={f.charAt(0).toUpperCase() + f.slice(1)}
                  size="small"
                  clickable
                  onClick={() => {
                    setStatusFilter((prev) => (prev === f ? undefined : f));
                    setPage(1);
                    setMobileOptionsDrawerOpen(false);
                  }}
                  sx={{
                    fontWeight: 600,
                    fontSize: 12,
                    bgcolor: statusFilter === f ? "primary.main" : undefined,
                    color:
                      statusFilter === f ? "primary.contrastText" : undefined,
                    "& .MuiChip-label": { px: 1.25 },
                  }}
                />
              ))}
            </Box>
          </Box>

          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={700}
              sx={{ display: "block", mb: 1.25 }}
            >
              Authentication
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {(["password", "key", "both"] as const).map((f) => (
                <Chip
                  key={`drawer-auth-${f}`}
                  label={f.charAt(0).toUpperCase() + f.slice(1)}
                  size="small"
                  clickable
                  onClick={() => {
                    setAuthFilter((prev) => (prev === f ? undefined : f));
                    setPage(1);
                    setMobileOptionsDrawerOpen(false);
                  }}
                  sx={{
                    fontWeight: 600,
                    fontSize: 12,
                    bgcolor: authFilter === f ? "primary.main" : undefined,
                    color:
                      authFilter === f ? "primary.contrastText" : undefined,
                    "& .MuiChip-label": { px: 1.25 },
                  }}
                />
              ))}
            </Box>
          </Box>

          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={700}
              sx={{ display: "block", mb: 1.25 }}
            >
              Access
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {(["read", "write"] as const).map((f) => (
                <Chip
                  key={`drawer-role-${f}`}
                  label={f.charAt(0).toUpperCase() + f.slice(1)}
                  size="small"
                  clickable
                  onClick={() => {
                    setRoleFilter((prev) => (prev === f ? undefined : f));
                    setPage(1);
                    setMobileOptionsDrawerOpen(false);
                  }}
                  sx={{
                    fontWeight: 600,
                    fontSize: 12,
                    bgcolor: roleFilter === f ? "primary.main" : undefined,
                    color:
                      roleFilter === f ? "primary.contrastText" : undefined,
                    "& .MuiChip-label": { px: 1.25 },
                  }}
                />
              ))}
            </Box>
          </Box>
        </Box>
      </Drawer>

      <HelperGenerateKeyPairDialog
        open={helperKeyDialogOpen}
        onClose={() => setHelperKeyDialogOpen(false)}
        onSuccess={() =>
          showToast("SSH key bundle (ZIP) downloaded.", "success")
        }
        onError={(message) => showToast(message, "error")}
      />

      <UnsavedPlatformDraftsDialog
        open={sftpLeaveDialogOpen}
        onClose={() => {
          setSftpLeaveDialogOpen(false);
          pendingSftpLeaveRef.current = null;
        }}
        title="Unsaved changes"
        intro="You have unsaved changes. Discard them to continue or stay to keep editing."
        rows={[]}
        discardButtonLabel="Discard and continue"
        onDiscardAndContinue={handleSftpLeaveDiscard}
      />
    </Layout>
  );
}
